import React, { useState, useEffect, Fragment } from "react";
import { Modal, Input, Select } from "antd";

import { FacilityStatusObj } from "@src/appV2/redesign/utils/facility";
import { logEvent } from "@src/appV2/lib/analytics";
import { ADMIN_EVENTS } from "../../constants/firebaseEvents";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_PROFILE } from "@src/modules/session";
import { SessionType } from "@src/modules/interface";

const { TextArea } = Input;
const { Option } = Select;

const statusData = {
  [`${FacilityStatusObj.ONBOARDING}`]: {
    buttonText: "Onboard",
    title: "Onboard Workplace",
    reasons: ["Billing Reconciliation Done", "Other, please explain"],
  },
  [`${FacilityStatusObj.ENROLLED}`]: {
    buttonText: "Enroll",
    title: "Enroll Workplace",
    reasons: ["Billing Reconciliation Done", "Other, please explain"],
  },
  [`${FacilityStatusObj.PROBATION}`]: {
    buttonText: "Put in Probation",
    title: "Workplace On Probation",
    reasons: [
      "Current Billing Failure - Hoping to Reconcile",
      "Past Billing Failure - Recovered & Monitoring",
      'Excessive "General Complaints" Filed',
      "Other, please explain",
    ],
  },
  [`${FacilityStatusObj.SUSPENDED}`]: {
    buttonText: "Suspend",
    title: "Suspend Workplace",
    reasons: [
      "Current Billing Failure - Hoping to Reconcile",
      "Pending Incident Review",
      "Other, please explain",
    ],
  },
  [`${FacilityStatusObj.TERMINATED}`]: {
    buttonText: "Terminate",
    title: "Terminate Workplace",
    reasons: [
      "Billing Failure & No Hope of Recovering",
      "Excessive Do-Not-Return Requests",
      "Minor or Major Offense",
      "Workplace chose to Terminate",
      "Other, please explain",
    ],
  },
  [`${FacilityStatusObj.PENDING}`]: {
    buttonText: "Put in Pending",
    title: "Workplace on Pending",
    reasons: [
      "Current Billing Failure - Hoping to Reconcile",
      "Usage Cap Reached",
      "Change of ownership",
    ],
  },
  [FacilityStatusObj.UNSIGNED]: {
    buttonText: "Put in Unsigned",
    title: "Workplace on Unsigned",
    reasons: ["Other, please explain"],
  },
};

function FacilityStatusModal({
  status,
  currentUserID,
  userID,
  visible,
  onCancel,
  saveFacilityStatus,
  lastLog,
  facility,
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  adminEmail,
}) {
  const dispatch = useDispatch();
  const { profile } = useSelector((state: SessionType) => state.session);
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requestData, setRequestData] = useState<any>({
    adminId: currentUserID,
    facilityId: userID,
    status,
  });
  const [isStatusDisabled, setStatusDisabled] = useState(true);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setRequestData((prevState) => ({
      ...prevState,
      reason: undefined,
      note: "",
      status,
      prevStatus: facility.status,
    }));
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, visible]);

  useEffect(() => {
    setStatusDisabled(
      !requestData.reason ||
        !requestData.note ||
        (status === lastLog.currentStatus && requestData.reason === lastLog.reason)
    );
  }, [requestData.reason, requestData.note, lastLog.currentStatus, lastLog.reason, status]);

  const onChangeHandler = (field) => (e) => {
    const value = field === "reason" ? e : e.target.value;
    setRequestData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleOnOk = async () => {
    setLoading(true);
    await saveFacilityStatus(requestData);
    logEvent(ADMIN_EVENTS.FACILITY_STATE_CHANGED, {
      stateChangeReason: requestData.reason,
    });
    dispatch({
      type: UPDATE_PROFILE,
      data: {
        ...profile,
        status,
      },
    });
    setLoading(false);
  };

  if (!status) {
    return <Fragment />;
  }

  const getOverriddenStatusReasons = () => {
    // reasons data object doesn't support dynamic reasons on moving between different status.
    if (
      status === FacilityStatusObj.PROBATION &&
      [FacilityStatusObj.TERMINATED, FacilityStatusObj.SUSPENDED].includes(facility.status)
    ) {
      // Need to do this, moving to probation from terminated doesn't require all
      // existing reasons, so overriding them here. `status` is current status
      // and `facility.status` is previousStatus
      return ["Past Billing Failure - Recovered & Monitoring", "Other, please explain"];
    }
    return statusData[status].reasons;
  };

  return (
    <Modal
      title={statusData[status].title}
      visible={visible}
      okText={statusData[status].buttonText}
      okButtonProps={{ disabled: isStatusDisabled || isLoading }}
      onOk={handleOnOk}
      onCancel={onCancel}
      confirmLoading={isLoading}
      // Rendered over the redesign FacilityDrawer (MUI Modal, zIndex 1300);
      // antd default 1000 would sit underneath.
      zIndex={1400}
    >
      <Select
        placeholder="Reason"
        defaultActiveFirstOption={false}
        filterOption={false}
        aria-label="Reason"
        defaultValue={status === lastLog.currentStatus ? lastLog.reason : undefined}
        style={{ width: "100%" }}
        onChange={onChangeHandler("reason")}
      >
        {getOverriddenStatusReasons().map((reason) => (
          <Option key={reason} value={reason}>
            {reason}
          </Option>
        ))}
      </Select>
      <br />
      <br />
      <TextArea
        style={{ marginTop: "5px" }}
        rows={4}
        defaultValue={status === lastLog.currentStatus ? lastLog.note : undefined}
        onChange={onChangeHandler("note")}
        placeholder="Notes"
      />
    </Modal>
  );
}

export { FacilityStatusModal };
